import React, { useMemo } from "react";
import { IMindmap, IMindmapArrow } from "../../api/mindmap";
import { getSnapPointCoords, calculateAutoPathWithSnaps, pathToSvg } from "./mindmapArrowUtils";
import corkboardBg from "../../assets/corkboard.jpg";
import "./MindmapDashboardPreview.css";

interface IMindmapDashboardPreviewProps {
    fileData: IMindmap;
}

const PADDING = 20;

const getShapePoints = (style: string, cx: number, cy: number, w: number, h: number): string => {
    const halfW = w / 2;
    const halfH = h / 2;
    
    switch (style) {
        case 'triangle':
            return `${cx},${cy - halfH} ${cx + halfW},${cy + halfH} ${cx - halfW},${cy + halfH}`;
        case 'trapezoid':
            const topInset = halfW * 0.25;
            return `${cx - halfW + topInset},${cy - halfH} ${cx + halfW - topInset},${cy - halfH} ${cx + halfW},${cy + halfH} ${cx - halfW},${cy + halfH}`;
        case 'star': {
            const outerR = Math.min(halfW, halfH);
            const innerR = outerR * 0.4;
            const points: string[] = [];
            for (let i = 0; i < 5; i++) {
                const outerAngle = (i * 72 - 90) * Math.PI / 180;
                const innerAngle = ((i * 72) + 36 - 90) * Math.PI / 180;
                points.push(`${cx + outerR * Math.cos(outerAngle)},${cy + outerR * Math.sin(outerAngle)}`);
                points.push(`${cx + innerR * Math.cos(innerAngle)},${cy + innerR * Math.sin(innerAngle)}`);
            }
            return points.join(' ');
        }
        default:
            return `${cx - halfW},${cy - halfH} ${cx + halfW},${cy - halfH} ${cx + halfW},${cy + halfH} ${cx - halfW},${cy + halfH}`;
    }
};

export const MindmapDashboardPreview = ({ fileData }: IMindmapDashboardPreviewProps) => {
    const objects = fileData.json?.objects || [];
    const arrows: IMindmapArrow[] = fileData.json?.arrows || [];
    const backgroundColor = fileData.json?.backgroundColor || '#fff5ec';
    const showDots = fileData.json?.showDots !== false;
    const backgroundType = fileData.json?.backgroundType || 'color';

    const viewBox = useMemo(() => {
        if (objects.length === 0) {
            return `0 0 200 150`;
        }

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        objects.forEach(obj => {
            const halfW = obj.width / 2;
            const halfH = obj.height / 2;
            const left = obj.position.x - halfW;
            const right = obj.position.x + halfW;
            const top = obj.position.y - halfH;
            const bottom = obj.position.y + halfH;

            minX = Math.min(minX, left);
            maxX = Math.max(maxX, right);
            minY = Math.min(minY, top);
            maxY = Math.max(maxY, bottom);
        });

        arrows.forEach(arrow => {
            const fromObj = objects[arrow.fromObjectIndex];
            const toObj = objects[arrow.toObjectIndex];
            if (!fromObj || !toObj) return;

            const start = getSnapPointCoords(fromObj, arrow.fromSnapPoint);
            const end = getSnapPointCoords(toObj, arrow.toSnapPoint);
            
            [start, end].forEach(pt => {
                minX = Math.min(minX, pt.x);
                maxX = Math.max(maxX, pt.x);
                minY = Math.min(minY, pt.y);
                maxY = Math.max(maxY, pt.y);
            });

            if (arrow.manualPath) {
                arrow.manualPath.forEach(pt => {
                    minX = Math.min(minX, pt.x);
                    maxX = Math.max(maxX, pt.x);
                    minY = Math.min(minY, pt.y);
                    maxY = Math.max(maxY, pt.y);
                });
            }
        });

        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;

        const vbX = minX - PADDING;
        const vbY = minY - PADDING;
        const vbW = Math.max(contentWidth + PADDING * 2, 50);
        const vbH = Math.max(contentHeight + PADDING * 2, 50);

        return `${vbX} ${vbY} ${vbW} ${vbH}`;
    }, [objects, arrows]);

    return (
        <svg 
            className="MindmapDashboardPreview" 
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                <pattern id="preview-dots" patternUnits="userSpaceOnUse" width="10" height="10">
                    <circle cx="5" cy="5" r="0.8" fill="#999" />
                </pattern>
                <linearGradient id="sticker-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#fffacd" />
                    <stop offset="100%" stopColor="#fff59d" />
                </linearGradient>
                <marker id="preview-arrowhead-thin" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#f90" />
                </marker>
                <marker id="preview-arrowhead-thick" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                    <polygon points="0 0, 12 4.5, 0 9" fill="#f90" />
                </marker>
                <marker id="preview-arrowhead-hollow" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                    <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#f90" strokeWidth="1" />
                </marker>
                <marker id="preview-arrowhead-straight-thin" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#f90" />
                </marker>
                <marker id="preview-arrowhead-straight-thick" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                    <polygon points="0 0, 12 4.5, 0 9" fill="#f90" />
                </marker>
                <marker id="preview-arrowhead-straight-hollow" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                    <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#f90" strokeWidth="1" />
                </marker>
                <marker id="preview-arrowhead-manual-thin" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#f90" />
                </marker>
                <marker id="preview-arrowhead-manual-thick" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                    <polygon points="0 0, 12 4.5, 0 9" fill="#f90" />
                </marker>
                <marker id="preview-arrowhead-manual-hollow" markerWidth="12" markerHeight="9" refX="11" refY="4.5" orient="auto">
                    <polygon points="0 0, 12 4.5, 0 9" fill="none" stroke="#f90" strokeWidth="1" />
                </marker>
            </defs>
            
            {backgroundType === 'cork' ? (
                <>
                    <image 
                        href={corkboardBg} 
                        x="-10000" 
                        y="-10000" 
                        width="20000" 
                        height="20000" 
                        preserveAspectRatio="xMidYMid slice" 
                    />
                </>
            ) : (
                <rect x="-10000" y="-10000" width="20000" height="20000" fill={backgroundColor} />
            )}
            {showDots && <rect x="-10000" y="-10000" width="20000" height="20000" fill="url(#preview-dots)" />}
            
            {objects.map((obj, index) => {
                const cx = obj.position.x;
                const cy = obj.position.y;
                const style = obj.style || 'box';
                const angle = obj.angle || 0;
                
                const fillColor = obj.fillColor || '#fc0';
                const strokeColor = obj.borderColor || '#f90';
                const strokeWidth = obj.borderWidth ?? 1;
                
                if (style === 'drawing' && obj.pathData) {
                    const offsetX = obj.drawingOffset?.x || 0;
                    const offsetY = obj.drawingOffset?.y || 0;
                    return (
                        <g key={index} transform={`translate(${offsetX}, ${offsetY})`}>
                            <path
                                d={obj.pathData}
                                stroke={obj.strokeColor || '#333'}
                                strokeWidth={obj.strokeWidth || 3}
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </g>
                    );
                }
                
                if (style === 'sticker') {
                    const halfW = obj.width / 2;
                    const halfH = obj.height / 2;
                    return (
                        <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                            <rect
                                x={cx - halfW}
                                y={cy - halfH}
                                width={obj.width}
                                height={obj.height}
                                fill="url(#sticker-gradient)"
                                stroke="#e0d78a"
                                strokeWidth={1}
                                rx={3}
                            />
                            <circle cx={cx} cy={cy - halfH + 6} r={4} fill="#d9534f" />
                            <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="10"
                                fill="#333"
                            >
                                {(obj.text || '').substring(0, 20)}
                            </text>
                        </g>
                    );
                }
                
                if (style === 'text') {
                    return (
                        <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                            <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="14"
                                fill="#333"
                                fontWeight="500"
                            >
                                {(obj.text || '').substring(0, 30)}
                            </text>
                        </g>
                    );
                }
                
                if (style === 'media') {
                    const halfW = obj.width / 2;
                    const halfH = obj.height / 2;
                    
                    if (obj.mediaType === 'image' && obj.mediaUrl) {
                        return (
                            <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                                <image
                                    x={cx - halfW}
                                    y={cy - halfH}
                                    width={obj.width}
                                    height={obj.height}
                                    href={obj.mediaUrl}
                                    preserveAspectRatio="xMidYMid slice"
                                    clipPath={`inset(0 round 4px)`}
                                />
                                <rect
                                    x={cx - halfW}
                                    y={cy - halfH}
                                    width={obj.width}
                                    height={obj.height}
                                    fill="none"
                                    stroke="#ccc"
                                    strokeWidth={1}
                                    rx={4}
                                />
                            </g>
                        );
                    }
                    
                    if (obj.mediaType === 'video') {
                        const playSize = Math.min(obj.width, obj.height) * 0.5;
                        return (
                            <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                                <rect
                                    x={cx - halfW}
                                    y={cy - halfH}
                                    width={obj.width}
                                    height={obj.height}
                                    fill="#2a2a2a"
                                    stroke="#444"
                                    strokeWidth={1}
                                    rx={4}
                                />
                                <polygon
                                    points={`${cx - playSize * 0.4},${cy - playSize * 0.5} ${cx + playSize * 0.6},${cy} ${cx - playSize * 0.4},${cy + playSize * 0.5}`}
                                    fill="#fff"
                                />
                            </g>
                        );
                    }
                    
                    const playBtnSize = Math.min(obj.width * 0.25, obj.height * 0.6);
                    const playX = cx - halfW + playBtnSize * 0.8;
                    const barStartX = cx - halfW + playBtnSize * 1.8;
                    const barEndX = cx + halfW - 8;
                    const barWidth = barEndX - barStartX;
                    return (
                        <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                            <rect
                                x={cx - halfW}
                                y={cy - halfH}
                                width={obj.width}
                                height={obj.height}
                                fill="#e8e8e8"
                                stroke="#ccc"
                                strokeWidth={1}
                                rx={4}
                            />
                            <polygon
                                points={`${playX - playBtnSize * 0.4},${cy - playBtnSize * 0.5} ${playX + playBtnSize * 0.5},${cy} ${playX - playBtnSize * 0.4},${cy + playBtnSize * 0.5}`}
                                fill="#666"
                            />
                            <rect
                                x={barStartX}
                                y={cy - 3}
                                width={barWidth}
                                height={6}
                                fill="#ccc"
                                rx={3}
                            />
                            <rect
                                x={barStartX}
                                y={cy - 3}
                                width={barWidth * 0.35}
                                height={6}
                                fill="#f90"
                                rx={3}
                            />
                            <circle
                                cx={barStartX + barWidth * 0.35}
                                cy={cy}
                                r={5}
                                fill="#f90"
                            />
                        </g>
                    );
                }
                
                if (style === 'folder') {
                    const halfW = obj.width / 2;
                    const halfH = obj.height / 2;
                    return (
                        <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                            <rect
                                x={cx - halfW}
                                y={cy - halfH}
                                width={obj.width}
                                height={obj.height}
                                fill="#fff8e1"
                                stroke="#ffc107"
                                strokeWidth={1}
                                rx={4}
                            />
                            <text
                                x={cx}
                                y={cy - 5}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="20"
                            >
                                📁
                            </text>
                            <text
                                x={cx}
                                y={cy + 12}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="8"
                                fill="#333"
                            >
                                {(obj.folderName || obj.text || '').substring(0, 10)}
                            </text>
                        </g>
                    );
                }
                
                if (style === 'round') {
                    return (
                        <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                            <ellipse
                                cx={cx}
                                cy={cy}
                                rx={obj.width / 2}
                                ry={obj.height / 2}
                                fill={fillColor}
                                stroke={strokeColor}
                                strokeWidth={strokeWidth}
                            />
                            <text
                                x={cx}
                                y={cy}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="12"
                                fill="#333"
                            >
                                {obj.text}
                            </text>
                        </g>
                    );
                }
                
                const points = getShapePoints(style, cx, cy, obj.width, obj.height);
                return (
                    <g key={index} transform={`rotate(${angle} ${cx} ${cy})`}>
                        <polygon
                            points={points}
                            fill={fillColor}
                            stroke={strokeColor}
                            strokeWidth={strokeWidth}
                        />
                        <text
                            x={cx}
                            y={cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="12"
                            fill="#333"
                        >
                            {obj.text}
                        </text>
                    </g>
                );
            })}
            
            {arrows.map((arrow, idx) => {
                const fromObj = objects[arrow.fromObjectIndex];
                const toObj = objects[arrow.toObjectIndex];
                if (!fromObj || !toObj) return null;
                
                const start = getSnapPointCoords(fromObj, arrow.fromSnapPoint);
                const end = getSnapPointCoords(toObj, arrow.toSnapPoint);
                const arrowStyle = arrow.style || 'thin';
                const isStraight = typeof arrowStyle === 'string' && arrowStyle.startsWith('straight-');
                const isManual = typeof arrowStyle === 'string' && arrowStyle.startsWith('manual-');
                const isBlock = arrowStyle === 'block';
                
                if (isBlock) {
                    const dx = end.x - start.x;
                    const dy = end.y - start.y;
                    const length = Math.hypot(dx, dy);
                    const angle = Math.atan2(dy, dx);
                    const arrowHeight = 24;
                    const arrowHeadLength = 20;
                    const bodyLength = Math.max(0, length - arrowHeadLength);
                    
                    const localPoints = [
                        { x: 0, y: -arrowHeight / 2 },
                        { x: bodyLength, y: -arrowHeight / 2 },
                        { x: bodyLength, y: -arrowHeight / 2 - 6 },
                        { x: length, y: 0 },
                        { x: bodyLength, y: arrowHeight / 2 + 6 },
                        { x: bodyLength, y: arrowHeight / 2 },
                        { x: 0, y: arrowHeight / 2 },
                    ];
                    
                    const rotatedPoints = localPoints.map(p => ({
                        x: start.x + p.x * Math.cos(angle) - p.y * Math.sin(angle),
                        y: start.y + p.x * Math.sin(angle) + p.y * Math.cos(angle)
                    }));
                    
                    const pointsStr = rotatedPoints.map(p => `${p.x},${p.y}`).join(' ');
                    const centerX = (start.x + end.x) / 2;
                    const centerY = (start.y + end.y) / 2;
                    const angleDeg = angle * 180 / Math.PI;
                    
                    return (
                        <g key={arrow.id || idx}>
                            <polygon points={pointsStr} fill="#f90" />
                            {arrow.text && (
                                <text
                                    x={centerX}
                                    y={centerY}
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    fontSize="10"
                                    fill="#fff"
                                    transform={`rotate(${angleDeg} ${centerX} ${centerY})`}
                                >
                                    {arrow.text}
                                </text>
                            )}
                        </g>
                    );
                }
                
                let path: { x: number; y: number }[];
                if (isManual && arrow.manualPath && arrow.manualPath.length >= 2) {
                    path = [...arrow.manualPath];
                    path[0] = start;
                    path[path.length - 1] = end;
                } else if (isStraight) {
                    path = [start, end];
                } else {
                    path = calculateAutoPathWithSnaps(
                        start, 
                        end, 
                        arrow.fromSnapPoint, 
                        arrow.toSnapPoint, 
                        objects, 
                        arrow.fromObjectIndex, 
                        arrow.toObjectIndex
                    ) || [start, end];
                }
                const d = pathToSvg(path);
                
                const baseStyle = isManual ? arrowStyle.replace('manual-', '') : (isStraight ? arrowStyle.replace('straight-', '') : arrowStyle);
                const strokeWidth = baseStyle === 'thin' ? 1 : baseStyle === 'thick' ? 3 : 2;
                const markerId = `preview-arrowhead-${arrowStyle}`;
                
                return (
                    <path 
                        key={arrow.id || idx}
                        d={d} 
                        stroke="#f90" 
                        strokeWidth={strokeWidth} 
                        fill="none"
                        markerEnd={`url(#${markerId})`}
                    />
                );
            })}
        </svg>
    );
};
